import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  Card,
  Divider,
  makeStyles,
  MenuItem,
  Typography,
  TextField,
  Tabs,
  Tab
} from '@material-ui/core'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import { Formik, Form } from 'formik'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import CustomTextField from 'components/Form/CustomTextField'
import { Collection, organizationStatus, Roles, SPONSOR_INFO_TEXT } from 'constants/index'
import { organizationFormSchema } from 'schemas/sponsor'
import { getSponsorshipLevels } from 'store/sponsorship-level'
import { omit } from 'lodash'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import { Add } from '@material-ui/icons'
import { colors } from 'utils/colors'
import CustomPopup from 'components/CustomPopup'
import SponsorshipLevelsForm from '../sponsorshipLevels/SponsorshipLevelsForm'
import InfoIconAlongText from 'components/InfoIconAlongText'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import OrganizationRepresentatives from './components/OrganizationRepresentatives'
import DataLoading from 'components/DataLoading'
import {
  addOrganization,
  fetchOrganizationById,
  resetOrganization,
  updateOrganizationById
} from 'store/organization'
import moment from 'moment/moment'

const OrgImageKeys = {
  logoImage: 'logoImageName',
  adImage: 'adImageName',
  brochureImage: 'brochureImageName'
}

// Tab content components
const TabPanel = (props) => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}>
      {value === index && <Box paddingTop={3}>{children}</Box>}
    </div>
  )
}

const a11yProps = (index) => {
  return {
    id: `organization-tab-${index}`,
    'aria-controls': `organization-tabpanel-${index}`
  }
}

// Reusable component for organization information fields
const SponsorInfoFields = (props) => {
  const {
    values,
    touched,
    errors,
    setFieldValue,
    sponsorshipLevels,
    setOpenSponsorShipLevelModal,
    handleFilesChange,
    handleUpload,
    handleDelete,
    organizationId,
    organization,
    user,
    classes
  } = props
  const sponsorshipLevelInfo = useMemo(() => {
    return sponsorshipLevels.find(
      (level) => level.id === (values.sponsorshipLevelId || organization?.sponsorshipLevelId)
    )
  }, [sponsorshipLevels, values.sponsorshipLevelId, organization?.sponsorshipLevelId])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={4}>
        <CustomTextField label="Organization Name" name="organization" type="text" />
      </Grid>
      <Grid item xs={12} md={4}>
        <CustomTextField label="Booth No" name="booth" type="text" />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          select
          fullWidth
          label="Sponsorship Level"
          variant="outlined"
          error={Boolean(touched.sponsorshipLevelId && errors.sponsorshipLevelId)}
          value={values.sponsorshipLevelId}
          onChange={(event) => {
            const selectedId = event.target.value
            setFieldValue('sponsorshipLevelId', selectedId)
          }}
          helperText={
            touched.sponsorshipLevelId && errors.sponsorshipLevelId
              ? errors.sponsorshipLevelId
              : null
          }>
          {sponsorshipLevels?.map((level) => (
            <MenuItem value={level.id} key={level.id}>
              <Typography>
                {level?.tierName} - {level?.sponsorshipTier}
              </Typography>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={() => setOpenSponsorShipLevelModal(true)}>
            <Typography className={classes.buttonItem}>
              <Add />
              Create sponsorship level
            </Typography>
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomTextField label="Website URL" name="websiteUrl" type="text" />
      </Grid>
      {sponsorshipLevelInfo?.allowAdBanner && (
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Banner Click URL"
            name="bannerClickUrl"
            type="text"
            infoText={SPONSOR_INFO_TEXT.bannerClickURL}
          />
        </Grid>
      )}
      <Grid item xs={12} lg={12}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label className={classes.label}>
            Introduction
            <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.introduction} />
          </label>
          <TipTapEditor
            value={values.introduction}
            onChange={(value) => setFieldValue('introduction', value)}
            placeholder="Write introduction here..."
            error={!!(touched?.introduction && errors?.introduction)}
            minHeight="150px"
            maxHeight="400px"
            hasPlaceholders={false}
          />
          <ErrorMessage message={touched?.introduction && errors?.introduction} />
        </Box>
      </Grid>
      <Grid item xs={12} lg={12}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label className={classes.label}>
            Message
            <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.message} />
          </label>
          <TipTapEditor
            value={values.message}
            onChange={(value) => setFieldValue('message', value)}
            placeholder="Write message here..."
            error={!!(touched?.message && errors?.message)}
            minHeight="150px"
            maxHeight="400px"
            hasPlaceholders={false}
          />
          <ErrorMessage message={touched?.message && errors?.message} />
        </Box>
      </Grid>
      {sponsorshipLevelInfo?.allowLogo && (
        <Grid item xs={12} lg={6}>
          <AvatarPicker
            title="Logo"
            subTitle="Allowed Format: PNG/JPG; Dimensions : 320x320"
            path={Collection.ORGANIZATIONS}
            handleCustomChange={(file) => handleFilesChange(OrgImageKeys.logoImage)(file)}
            onUpload={(name) => handleUpload(OrgImageKeys.logoImage)(name)}
            onDelete={() => handleDelete(OrgImageKeys.logoImage)()}
            imageName={organizationId ? organization?.logoImageName : null}
            resetImage={!(organizationId && organization?.logoImageName)}
            conferenceId={user?.role !== Roles.SUPER_ADMIN ? user?.conferenceId : ''}
            infoText={SPONSOR_INFO_TEXT.logo}
            firsTimeUpload={!organizationId}
          />
        </Grid>
      )}
      {sponsorshipLevelInfo?.allowAdBanner && (
        <Grid item xs={12} lg={6}>
          <AvatarPicker
            title="Ad Banner"
            subTitle="Allowed Format: PNG/JPG; Recommended Dimensions : 300x100"
            path={Collection.ORGANIZATIONS}
            handleCustomChange={(file) => handleFilesChange(OrgImageKeys.adImage)(file)}
            onUpload={(name) => handleUpload(OrgImageKeys.adImage)(name)}
            onDelete={() => handleDelete(OrgImageKeys.adImage)()}
            imageName={organizationId ? organization?.adImageName : null}
            resetImage={!(organizationId && organization?.adImageName)}
            conferenceId={user?.conferenceId}
            infoText={SPONSOR_INFO_TEXT.adBanner}
            firsTimeUpload={!organizationId}
          />
        </Grid>
      )}
      {sponsorshipLevelInfo?.allowBrochure && (
        <Grid item xs={12} lg={6}>
          <AvatarPicker
            title="Brochure"
            subTitle="Allowed Format: PNG/JPG; Dimensions : 320x50"
            path={Collection.ORGANIZATIONS}
            handleCustomChange={(file) => handleFilesChange(OrgImageKeys.brochureImage)(file)}
            onUpload={(name) => handleUpload(OrgImageKeys.brochureImage)(name)}
            onDelete={() => handleDelete(OrgImageKeys.brochureImage)()}
            imageName={organizationId ? organization?.brochureImageName : null}
            resetImage={!(organizationId && organization?.brochureImageName)}
            conferenceId={user?.role !== Roles.SUPER_ADMIN ? user?.conferenceId : ''}
            infoText={SPONSOR_INFO_TEXT.brochure}
            firsTimeUpload={!organizationId}
          />
        </Grid>
      )}
    </Grid>
  )
}

const OrganizationForm = ({ displayContext, onSuccess, onCloseModal }) => {
  const classes = useStyles()
  const { organizationId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [openSponsorShipLevelModal, setOpenSponsorShipLevelModal] = useState(false)

  // Use URL search params for tab selection
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(tabParam === 'representatives' ? 1 : 0)

  const { user } = useSelector((state) => state.auth)
  const { organization } = useSelector((state) => state.organization)
  const { sponsorshipLevels, inProgress: sponsorshipLevelProgress } = useSelector(
    (state) => state.sponsorshipLevel
  )

  const initialValues = {
    organization: organization?.organization ?? '',
    booth: organization?.booth ?? '',
    sponsorshipLevelId: organization?.sponsorshipLevelId ?? '',
    websiteUrl: organization?.websiteUrl ?? '',
    introduction: organization?.introduction ?? '',
    message: organization?.message ?? '',
    bannerClickUrl: organization?.bannerClickUrl ?? '',
    // Image names
    logoImageName: organization?.logoImageName ?? null,
    adImageName: organization?.adImageName ?? null,
    brochureImageName: organization?.brochureImageName ?? null
  }

  const [adImageFile, setAdImageFile] = useState(null)
  const [logoImageFile, setLogoImageFile] = useState(null)
  const [brochureImageFile, setBrochureImageFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [performingAction, setPerformingAction] = useState(false)

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    // Update the URL search params when tab changes
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', newValue === 0 ? 'info' : 'representatives')
    history.replace({
      pathname: location.pathname,
      search: newSearchParams.toString()
    })
  }

  const goBack = () => {
    dispatch(resetOrganization())
    history.goBack()
  }

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      await dispatch(fetchOrganizationById(organizationId))
      setLoading(false)
    }

    if (organizationId) {
      fetch()
    }
    return () => dispatch(resetOrganization())
  }, [dispatch, organizationId])

  useEffect(() => {
    dispatch(getSponsorshipLevels(user?.conferenceId))
  }, [dispatch, user?.conferenceId])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tab = searchParams.get('tab')
    if (tab === 'representatives') {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [location.search])

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal && onCloseModal()
    }
  }

  const handleFilesChange = (key) => (file) => {
    if (key === OrgImageKeys.logoImage) {
      setLogoImageFile(file)
    } else if (key === OrgImageKeys.adImage) {
      setAdImageFile(file)
    } else if (key === OrgImageKeys.brochureImage) {
      setBrochureImageFile(file)
    }
  }

  const handleUpload = (key) => async (name) => {
    const modifiedOrg = omit(organization, 'id')
    if (key === OrgImageKeys.logoImage) {
      modifiedOrg.logoImageName = name
    } else if (key === OrgImageKeys.adImage) {
      modifiedOrg.adImageName = name
    } else if (key === OrgImageKeys.brochureImage) {
      modifiedOrg.brochureImageName = name
    }
    await updateOrganizationById(organizationId, modifiedOrg, user?.conferenceId)
  }

  const handleDelete = (key) => async () => {
    const modifiedOrg = omit(organization, 'id')
    if (key === OrgImageKeys.logoImage) {
      modifiedOrg.logoImageName = null
    } else if (key === OrgImageKeys.adImage) {
      modifiedOrg.adImageName = null
    } else if (key === OrgImageKeys.brochureImage) {
      modifiedOrg.brochureImageName = null
    }
    await updateOrganizationById(organizationId, modifiedOrg, user?.conferenceId)
  }

  const forApproval = useMemo(
    () =>
      organizationId === organization?.id &&
      organization?.status === organizationStatus.PENDING_DATA_REVIEW,
    [organization, organizationId]
  )

  const onSubmit = async (values) => {
    try {
      setPerformingAction(true)
      const payload = {
        organization: values.organization.trim(),
        booth: values.booth.trim(),
        sponsorshipLevelId: values.sponsorshipLevelId,
        websiteUrl: values.websiteUrl.trim(),
        introduction: values.introduction.trim(),
        message: values.message.trim(),
        bannerClickUrl: values.bannerClickUrl.trim()
      }

      if (forApproval) {
        payload.status = organizationStatus.APPROVED
        payload.approvedAt = moment().utc().valueOf()
      }

      // Handle image uploads for a new organization
      if (!organizationId) {
        if (logoImageFile) {
          payload.logoImageName = await uploadImageFirstTime(
            logoImageFile,
            Collection.ORGANIZATIONS,
            user?.conferenceId
          )
        }
        if (adImageFile) {
          payload.adImageName = await uploadImageFirstTime(
            adImageFile,
            Collection.ORGANIZATIONS,
            user?.conferenceId
          )
        }
        if (brochureImageFile) {
          payload.brochureImageName = await uploadImageFirstTime(
            brochureImageFile,
            Collection.ORGANIZATIONS,
            user?.conferenceId
          )
        }
      }

      if (organizationId) {
        await updateOrganizationById(organizationId, payload, user?.conferenceId)
        if (forApproval) {
          toast.success('Organization approved.')
        } else {
          toast.success('Organization updated.')
        }

        if (onSuccess) {
          onSuccess(organizationId)
        }
      } else {
        const id = await addOrganization(
          { status: organizationStatus.INCOMPLETE, ...payload },
          user?.conferenceId
        )

        if (id && onSuccess) {
          onSuccess(id)
        }
      }
    } catch (error) {
      console.error('Error saving organization:', error)
      toast.error('Could not save organization. Try again.')
    } finally {
      setPerformingAction(false)

      if (displayContext === 'modal') {
        onCloseModal && onCloseModal()
      } else {
        history.goBack()
      }
    }
  }

  if (sponsorshipLevelProgress || loading) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <DataLoading />
      </div>
    )
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={organizationFormSchema({ showSponsorshipLevel: true })}
        onSubmit={onSubmit}>
        {({ values, setFieldValue, touched, errors }) => (
          <Form>
            {displayContext === 'page' && (
              <ViewHeader
                title={
                  forApproval
                    ? 'Review & Approve Organization'
                    : organizationId
                    ? 'Edit Organization'
                    : 'Create Organization'
                }>
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button variant="outlined" onClick={goBack}>
                    Back
                  </Button>
                  {(activeTab === 0 || !organizationId) && (
                    <>
                      <ProgressButton
                        type="submit"
                        label={forApproval ? 'Save & Approve' : 'Save'}
                        size="small"
                        inProgress={performingAction}
                        disabled={performingAction}
                      />
                    </>
                  )}
                </Box>
              </ViewHeader>
            )}

            {!organizationId ? (
              // For new sponsors or approval, show only the single form without tabs
              <SponsorInfoFields
                values={values}
                touched={touched}
                errors={errors}
                setFieldValue={setFieldValue}
                sponsorshipLevels={sponsorshipLevels}
                setOpenSponsorShipLevelModal={setOpenSponsorShipLevelModal}
                handleFilesChange={handleFilesChange}
                handleUpload={handleUpload}
                handleDelete={handleDelete}
                organizationId={organizationId}
                organization={organization}
                user={user}
                classes={classes}
              />
            ) : (
              // For existing sponsors, show the tabbed interface
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    className={classes.tabs}
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="organization tabs">
                    <Tab label="Organization Info" {...a11yProps(0)} />
                    <Tab label="Organization Representatives" {...a11yProps(1)} />
                  </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                  <SponsorInfoFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    sponsorshipLevels={sponsorshipLevels}
                    setOpenSponsorShipLevelModal={setOpenSponsorShipLevelModal}
                    handleFilesChange={handleFilesChange}
                    handleUpload={handleUpload}
                    handleDelete={handleDelete}
                    organizationId={organizationId}
                    organization={organization}
                    user={user}
                    classes={classes}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <OrganizationRepresentatives
                    organizationId={organizationId}
                    conferenceId={user?.conferenceId}
                    organization={organization}
                  />
                </TabPanel>
              </>
            )}

            {(activeTab === 0 ||
              !organizationId ||
              (organizationId && forApproval && activeTab === 0)) && (
              <Grid container justifyContent="flex-end" spacing={3}>
                <Grid item>
                  <Button size="large" disabled={performingAction} onClick={onCancel}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <ProgressButton
                    type="submit"
                    label={forApproval ? 'Save & Approve' : 'Save'}
                    inProgress={performingAction}
                    disabled={performingAction}
                  />
                </Grid>
              </Grid>
            )}

            <CustomPopup
              title="Create sponsorship level"
              maxWidth="md"
              open={openSponsorShipLevelModal}
              onClose={() => setOpenSponsorShipLevelModal(false)}>
              <SponsorshipLevelsForm
                displayContext="modal"
                onCloseModal={() => setOpenSponsorShipLevelModal(false)}
                onSuccess={(sponsorshipLevelId) => {
                  setFieldValue('sponsorshipLevelId', sponsorshipLevelId)
                  setOpenSponsorShipLevelModal(false)
                }}
              />
            </CustomPopup>
          </Form>
        )}
      </Formik>
    </Card>
  )
}
export default OrganizationForm

const useStyles = makeStyles((theme) => ({
  tabs: {
    marginTop: '20px',
    '& .MuiTab-root.MuiTab-labelIcon': {
      minHeight: '35px'
    },
    '& .MuiTab-root svg': {
      height: '20px',
      width: '20px',
      marginBottom: '0px !important'
    },
    '& .MuiTab-root .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      alignItems: 'center'
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(51,220,235,0.16)'
    }
  },
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem'
  },
  styledCard: {
    paddingTop: '1rem'
  },
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  }
}))
